import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { useAccountRoleName } from './useAccountRoleName';

export const URLS: Record<CloudAccountType, string> = {
  [CloudAccountType.Source]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/source-account.yml',
  [CloudAccountType.Vault]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/eon-vault-verification-role.yml',
  [CloudAccountType.Restore]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/restore-account.yml',
  [CloudAccountType.Scanning]:
    'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/scanning-account.yml',
  [CloudAccountType.AccountTypeUnspecified]: '',
};

export const useCreateAwsAccount = (
  accountType: CloudAccountType,
  onDone: (accountId: string) => void
) => {
  const dal = useDAL();
  const [accountId, setAccountId] = useState('');
  const [roleName, setRoleName] = useAccountRoleName(accountType);
  const [orgRoleName, setOrgRoleName] = useState(`EonOrganizationAccountRole`);
  const [organizationOUID, setOrganizationOUID] = useState(``);
  const [isCreationInProgress, setIsCreationInProgress] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [hasError, setHasError] = useState(false);

  const connectOrganization = async () => {
    setIsCreationInProgress(true);
    const { id, success } = await dal.cloudAccounts.sourceAwsOrganizationalUnit
      .connect({
        roleArn: `arn:aws:iam::${accountId}:role/${orgRoleName}`,
        providerOrganizationalUnitId: organizationOUID,
      })
      .then((res) => ({ success: true, id: res.organizationalUnit.id }))
      .catch(() => ({ success: false, id: '' }));
    setIsCreationInProgress(false);
    if (success) {
      setIsCreated(true);
      setTimeout(() => onDone(id), 1000);
    } else {
      setHasError(true);
    }
  };

  const createAccount = async () => {
    setIsCreationInProgress(true);

    const key = accountType.toLowerCase() as
      | 'source'
      | 'restore'
      | 'vault'
      | 'scanning';

    const { id, success } = await dal.cloudAccounts[key]
      .create({
        cloudProvider: Provider.Aws,
        role: `arn:aws:iam::${accountId}:role/${roleName}`,
        // name: accountId, TODO: allow the user to provide a pretty name and send it here
        regions:
          accountType === CloudAccountType.Restore ||
          accountType === CloudAccountType.Vault
            ? ['us-east-1']
            : undefined,
        isCustomerOwned:
          accountType === CloudAccountType.Vault ||
          accountType === CloudAccountType.Scanning,
      })
      .then((res) => ({ success: true, id: res.id }))
      .catch(() => ({ success: false, id: '' }));
    setIsCreationInProgress(false);
    if (success) {
      setIsCreated(true);
      setTimeout(() => onDone(id), 1000);
    } else {
      setHasError(true);
    }
  };

  return {
    accountId,
    setAccountId,
    roleName,
    orgRoleName,
    setRoleName,
    setOrgRoleName,
    organizationOUID,
    setOrganizationOUID,
    isCreationInProgress,
    isCreated,
    hasError,
    setHasError,
    retry: () => setHasError(false),
    createAccount,
    connectOrganization,
  };
};
